import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, IconButton, Typography, TablePagination, TextField, useTheme, useMediaQuery, CardContent, Stack, InputAdornment, Tooltip
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import transporteService, { Transporte } from '../../services/transporteService';

const Transportes: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [transportes, setTransportes] = useState<Transporte[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchTransportes = async (pageNum: number = page, pageSize: number = rowsPerPage, search: string = searchTerm) => {
    try {
      setLoading(true);
      const data = await transporteService.getTransportesPaged({
        pageNumber: pageNum + 1,
        pageSize: pageSize,
        searchTerm: search
      });
      setTransportes(data.items);
      setTotalCount(data.totalCount);
    } catch (error: any) {
      console.error('Error al cargar transportes:', error);
      enqueueSnackbar('Error al cargar transportes', { variant: 'error' });
      setTransportes([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransportes();
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPage(0);
      fetchTransportes(0, rowsPerPage, searchTerm);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    fetchTransportes(newPage, rowsPerPage, searchTerm);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchTransportes(0, newRowsPerPage, searchTerm);
  };

  const handleDelete = async (nroExpreso: number | string) => {
    if (window.confirm('¿Está seguro de eliminar este transporte?')) {
      try {
        await transporteService.deleteTransporte(nroExpreso);
        enqueueSnackbar('Transporte eliminado exitosamente', { variant: 'success' });
        fetchTransportes();
      } catch (error: any) {
        console.error('Error al eliminar transporte:', error);
        enqueueSnackbar(error?.response?.data?.message || 'Error al eliminar transporte', { variant: 'error' });
      }
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Transportes
            </Typography>
            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => navigate('/configuraciones/transportes/nuevo')}>
              Nuevo Transporte
            </Button>
          </Stack>

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#1976d2' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#f8f9fa' } }}
            />
          </Box>

          {isMobile ? (
            <Stack spacing={2}>
              {transportes.map((transporte) => (
                <Card key={transporte.nroExpreso} variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {transporte.nroExpreso} - {transporte.nombreExpreso || transporte.nombre}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Forma Entrega: {transporte.formaEntrega || '-'}
                    </Typography>
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <IconButton size="small" onClick={() => navigate(`/configuraciones/transportes/${transporte.nroExpreso}`)} color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(transporte.nroExpreso!)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </Stack>
                </Card>
              ))}
            </Stack>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>N° Expreso</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Nombre</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Forma Entrega</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transportes.map((transporte) => (
                    <TableRow key={transporte.nroExpreso} hover>
                      <TableCell>{transporte.nroExpreso}</TableCell>
                      <TableCell>{transporte.nombreExpreso || transporte.nombre}</TableCell>
                      <TableCell>{transporte.formaEntrega || '-'}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => navigate(`/configuraciones/transportes/${transporte.nroExpreso}`)} color="primary">
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" onClick={() => handleDelete(transporte.nroExpreso!)} color="error">
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Transportes;
